import React from "react"
import clsx from "clsx"
import { Link, useLocation } from "react-router-dom"
import useStyles from "@/components/MenuItemWithPopper/styles"
import {
	Grid,
	ListItem,
	ListItemIcon,
	ListItemText,
	Paper,
	Popper,
	List,
	Typography,
	ListSubheader,
	Link as MuiLink,
	Tooltip
} from "@material-ui/core"
import { InfoOutlined as InfoIcon } from "@material-ui/icons"

import ListItemMenu from "@/components/ListItemMenu"
import { MenuItem, MenuCategory as MenuCategoryItem } from "@/hooks/useDashboardMenuData"
import { colors } from "@/styles/colors"

export type MenuSubItem = MenuItem

interface MenuItemWithPopperProps {
	item: MenuCategoryItem
	anchorRefs: React.MutableRefObject<Record<string, HTMLElement | null>>
	hoveredCategory: string | null
	handleEnter: (id: string) => void
	handleLeave: () => void
	handleClick?: (id: string, e?: React.MouseEvent) => void
	isDisabled?: boolean
	isSidebarOpen: boolean
}

const MenuItemWithPopper: React.FC<MenuItemWithPopperProps> = ({
	item,
	anchorRefs,
	hoveredCategory,
	handleEnter,
	handleLeave,
	handleClick,
	isDisabled,
	isSidebarOpen
}) => {
	const classes = useStyles()
	const shouldShowPopper = item.hasPopper !== false && item.items && item.items.length > 0
	const labelClass = item.isSecondary ? classes.listItemTextMuted : classes.listItemText

	const location = useLocation()

	const isMenuItemActive = (route?: string) => {
		if (!route) return false
		return location.pathname.includes(route)
	}

	if (!shouldShowPopper && item.to) {
		if (item.isExternal) {
			return (
				<MuiLink
					href={item.to}
					className={classes.link}
					target="_blank"
					rel="noopener noreferrer"
					style={{ textDecoration: "none", display: "block" }}
				>
					<ListItem
						button
						className={clsx(classes.categoryItem, { sidebarOpen: isSidebarOpen })}
						disabled={isDisabled}
						style={{ minHeight: isSidebarOpen ? "auto" : 48 }}
					>
						<ListItemIcon className={classes.listItemIcon}>{item.icon}</ListItemIcon>
						<ListItemText>
							<Typography variant="inherit" className={labelClass}>
								{isSidebarOpen && item.label}
							</Typography>
						</ListItemText>
					</ListItem>
				</MuiLink>
			)
		}

		return (
			<Link
				to={item.to}
				className={classes.link}
				style={{ textDecoration: "none", display: "block" }}
			>
				<ListItem
					button
					className={clsx(
						classes.categoryItem,
						{ sidebarOpen: isSidebarOpen },
						{
							[classes.listItemActive]: isMenuItemActive(item.mainRoute) && isSidebarOpen
						}
					)}
					disabled={isDisabled}
					style={{ minHeight: isSidebarOpen ? "auto" : 48 }}
				>
					<ListItemIcon className={classes.listItemIcon}>{item.icon}</ListItemIcon>
					<ListItemText>
						<Typography className={labelClass}>
							{isSidebarOpen ? item.label : ""}
						</Typography>
					</ListItemText>
				</ListItem>
			</Link>
		)
	}

	return (
		<Grid container direction="column">
			<Grid item>
				<ListItem
					button
					onMouseEnter={() => handleEnter(item.id)}
					onMouseLeave={handleLeave}
					onClick={(event) => { event.stopPropagation(); event.preventDefault && event.preventDefault(); handleClick?.(item.id, event) }}
					ref={(element) => (anchorRefs.current[item.id] = element)}
					className={clsx(
						classes.categoryItem,
						{ sidebarOpen: isSidebarOpen },
						{
							[classes.listItemActive]: isMenuItemActive(item.mainRoute) && isSidebarOpen
						}
					)}
					disabled={isDisabled}
					style={{ minHeight: isSidebarOpen ? "auto" : 48, cursor: "default" }}
				>
					<ListItemIcon className={classes.listItemIcon}>{item.icon}</ListItemIcon>
					<ListItemText>
						<Typography className={labelClass}>
							{isSidebarOpen ? item.label : ""}
						</Typography>
					</ListItemText>
				</ListItem>
			</Grid>
			<Grid item>
				<Popper
					open={hoveredCategory === item.id}
					anchorEl={anchorRefs.current[item.id]}
					placement={item.placement || "right-start"}
					disablePortal={false}
					style={{ zIndex: 1300 }}
					onMouseEnter={() => handleEnter(item.id)}
					onMouseLeave={handleLeave}
				>
					<Paper className={classes.popperPaper} elevation={3}>
						<List>
							<ListSubheader disableSticky>{item.label}</ListSubheader>
							{item.items?.map((subitem) => {
								const highlightedColor = colors.green[600]
								const highlightedStyle = subitem.isHighlighted
									? { fontWeight: 700, color: highlightedColor }
									: undefined
								const iconElement = (subitem.isHighlighted && subitem.icon)
									? React.cloneElement(subitem.icon as React.ReactElement, { style: { color: highlightedColor } })
									: subitem.icon

								const itemContent = subitem?.onClick
									? <ListItemMenu
										key={subitem.label}
										text={subitem.label}
										featureType={subitem.featureType}
										to={subitem.to}
										isExternalLink={subitem.isExternal}
										isDisabled={subitem.isDisabled}
										listItemIcon={
											<ListItemIcon style={{ minWidth: 40 }}>
												{iconElement}
											</ListItemIcon>
										}
										listItemText={
											<ListItemText>
												<Typography
													className={classes.listItemText}
													style={highlightedStyle}
												>
													{subitem.label}
												</Typography>
											</ListItemText>
										}
										onClick={subitem.onClick}
									/>
									: <Link
										key={subitem.label}
										to={subitem.to || "/"}
										className={classes.link}
										target={subitem.isExternal ? "_blank" : "_self"}
										style={subitem.isDisabled ? { pointerEvents: "none" } : undefined}
									>
										<ListItemMenu
											text={subitem.label}
											featureType={subitem.featureType}
											to={subitem.to}
											isExternalLink={subitem.isExternal}
											isDisabled={subitem.isDisabled}
											listItemIcon={
												<ListItemIcon style={{ minWidth: 40 }}>
													{iconElement}
												</ListItemIcon>
											}
											listItemText={
												<ListItemText>
													<Typography
														className={classes.listItemText}
														style={highlightedStyle}
													>
														{subitem.label}
													</Typography>
												</ListItemText>
											}
											onClick={subitem.onClick}
										/>
									</Link>

								if (subitem.isDisabled && subitem.disabledTooltip) {
									return (
										<Grid key={subitem.label} container alignItems="center" justifyContent="space-between" wrap="nowrap">
											<Grid item className={classes.disabledItem} style={{ flex: 1, minWidth: 0 }}>
												{itemContent}
											</Grid>
											<Grid item className={classes.disabledInfoIcon}>
												<Tooltip
													title={subitem.disabledTooltip}
												>
													<InfoIcon fontSize="small" htmlColor={colors.gray[400]} />
												</Tooltip>
											</Grid>
										</Grid>
									)
								}

								return itemContent
							})}
						</List>
					</Paper>
				</Popper>
			</Grid>
		</Grid>
	)
}

export default MenuItemWithPopper
